// Builds the Student Overview CSV (one row per student) with per-mock
// Pass / Fail / Absent columns. Shared by Admin, Super Admin and Head HR.

const PASS_RATIO = 0.35;

export const escapeCSV = (val) => {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
};

const findStudent = (studentId, testData) =>
  testData?.studentOverviews?.find((s) => s.studentId === studentId);

const fmtRatio = (done, total, pct) =>
  `${done || 0}/${total || 0} (${pct || 0}%)`;

const dateKey = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fmtDate = (key) => {
  if (!key) return 'No date';
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
};

const testKey = (test) =>
  `${(test.subjectName || '').trim().toLowerCase()}|${dateKey(test.testDate)}`;

// Distinct tests taken by anyone in the batch, sorted by date then subject
const collectTests = (testData) => {
  const tests = new Map();
  (testData?.studentOverviews || []).forEach((s) =>
    (s.testStatus || []).forEach((t) => {
      const key = testKey(t);
      if (!tests.has(key)) {
        tests.set(key, {
          key,
          subjectName: (t.subjectName || '').trim(),
          date: dateKey(t.testDate),
        });
      }
    })
  );
  return [...tests.values()].sort(
    (a, b) =>
      a.date.localeCompare(b.date) || a.subjectName.localeCompare(b.subjectName)
  );
};

const isAbsent = (result) =>
  !result ||
  result.Marks == null ||
  String(result.status || '').toLowerCase() === 'absent';

// Returns [cells..., present, absent, passed, failed] for one student
const mockCells = (tests, studentMock) => {
  const byKey = new Map();
  (studentMock?.testStatus || []).forEach((t) => {
    const key = testKey(t);
    // Keep the attempt with marks if a student has duplicates
    if (!byKey.has(key) || (byKey.get(key).Marks == null && t.Marks != null)) {
      byKey.set(key, t);
    }
  });

  let present = 0;
  let absent = 0;
  let passed = 0;
  let failed = 0;

  const cells = tests.map(({ key }) => {
    const result = byKey.get(key);
    if (isAbsent(result)) {
      absent += 1;
      return 'Absent';
    }
    present += 1;
    if (!result.totalMarks) return `Present (${result.Marks} marks)`;
    const ratio = result.Marks / result.totalMarks;
    const pct = `${(ratio * 100).toFixed(1)}%`;
    if (ratio >= PASS_RATIO) {
      passed += 1;
      return `Pass (${pct})`;
    }
    failed += 1;
    return `Fail (${pct})`;
  });

  return [...cells, present, absent, passed, failed];
};

const mockHeaders = (tests, label) => [
  ...tests.map((t) => `${label} - ${t.subjectName} (${fmtDate(t.date)})`),
  `${label} Present`,
  `${label} Absent`,
  `${label} Passed`,
  `${label} Failed`,
];

export const buildStudentOverviewCsv = ({
  students,
  mcqData,
  qaData,
  mcqAssignmentData,
  qaAssignmentData,
  mockData,
  finalMockData,
  findPlacementData,
}) => {
  const mockTests = collectTests(mockData);
  const finalMockTests = collectTests(finalMockData);

  const header = [
    'Sr No',
    'Name',
    'Email',
    'Contact',
    'Course',
    'Practical Assignment',
    'MCQ Assignment',
    'Q&A Assignment',
    'MCQ Test',
    'Q&A Test',
    'Attendance',
    'Offer Status',
    ...mockHeaders(mockTests, 'Mock'),
    ...mockHeaders(finalMockTests, 'Final Mock'),
  ];

  const body = students.map((student, index) => {
    const id = student.studentId;
    const mcqAssignment = findStudent(id, mcqAssignmentData)?.statistics;
    const qaAssignment = findStudent(id, qaAssignmentData)?.statistics;
    const mcqTest = findStudent(id, mcqData)?.statistics;
    const qaTest = findStudent(id, qaData)?.statistics;
    const attendance = student.attendanceDetails;
    const placement = findPlacementData(student);

    return [
      index + 1,
      student.name,
      student.email,
      student.contactNumber,
      student.courseName || '',
      fmtRatio(
        student.statistics?.submittedAssignments,
        student.statistics?.totalAssignments,
        student.statistics?.submissionRate
      ),
      fmtRatio(
        mcqAssignment?.completedAssignments,
        mcqAssignment?.totalAssignments,
        mcqAssignment?.overallPercentage
      ),
      fmtRatio(
        qaAssignment?.completedAssignments,
        qaAssignment?.totalAssignments,
        qaAssignment?.overallPercentage
      ),
      fmtRatio(
        mcqTest?.completedTests,
        mcqTest?.totalTests,
        mcqTest?.overallPercentage
      ),
      fmtRatio(qaTest?.completedTests, qaTest?.totalTests, qaTest?.overallPercentage),
      attendance
        ? fmtRatio(
            attendance.presentLectures,
            attendance.totalLectures,
            attendance.attendancePercentage
          )
        : '-',
      placement
        ? placement.offerStatus?.hasOffer
          ? 'Has Offer'
          : 'No Offer'
        : '-',
      ...mockCells(mockTests, findStudent(id, mockData)),
      ...mockCells(finalMockTests, findStudent(id, finalMockData)),
    ];
  });

  return [header, ...body]
    .map((row) => row.map(escapeCSV).join(','))
    .join('\n');
};

export const studentOverviewFileName = (batchInfo) => {
  const parts = ['student-overview'];
  if (batchInfo?.courseName) parts.push(batchInfo.courseName);
  if (batchInfo?.batchNumber != null) parts.push(`B${batchInfo.batchNumber}`);
  parts.push(new Date().toISOString().slice(0, 10));
  return `${parts.join('-').replace(/[^\w.-]+/g, '_')}.csv`;
};

export const downloadCsv = (csv, fileName) => {
  const blob = new Blob(['﻿' + csv], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
